import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core'
import { FormControlStatus, ValidationErrors } from '@angular/forms'
import { DestroySubscribers } from '@ftr/foundation'
import { compact, flatten } from 'lodash-es'
import { Observable, takeUntil } from 'rxjs'

const DEFAULT_RECOMMENDATIONS = [
  'A minimum of 12 characters is required.',
  'Avoid using common words and phrases.',
  'Avoid reusing passwords.',
]

const STRONG_RECOMMENDATIONS = [
  'This is a strong password, you are free to continue.',
  'We recommend using a password manager to keep your passwords safe.',
]

@Component({
  selector: 'ftr-password-validation-feedback',
  templateUrl: './password-validation-feedback.component.html',
  styleUrls: ['./password-validation-feedback.component.css'],
})
export class PasswordValidationFeedbackComponent extends DestroySubscribers implements OnInit, OnChanges {
  @Input() validationErrors: ValidationErrors | null
  @Input() confirmPasswordValidationErrors?: ValidationErrors | null

  @Input() formStatusChanges$: Observable<FormControlStatus>

  protected passwordRecommendations: string[]
  private formPending: boolean = false
  private cachedPasswordRecommendations?: string[] | null

  ngOnInit(): void {
    this.passwordRecommendations = DEFAULT_RECOMMENDATIONS
    this.formStatusChanges$.pipe(takeUntil(this.finalize)).subscribe(formStatus => {
      this.formPending = formStatus === 'PENDING'
      if (!this.formPending && this.cachedPasswordRecommendations) {
        this.passwordRecommendations = this.cachedPasswordRecommendations
        this.cachedPasswordRecommendations = null
      }
    })
  }

  ngOnChanges(changes: SimpleChanges): void {
    const recommendations = this.updateNormalisedValidationErrors(
      changes.validationErrors ? changes.validationErrors.currentValue : this.validationErrors,
      changes.confirmPasswordValidationErrors
        ? changes.confirmPasswordValidationErrors.currentValue
        : this.confirmPasswordValidationErrors,
    )
    if (this.formPending) {
      this.cachedPasswordRecommendations = recommendations
    } else {
      this.passwordRecommendations = recommendations
    }
  }

  protected updateNormalisedValidationErrors(
    newErrors?: ValidationErrors | null,
    confirmPasswordErrors?: ValidationErrors | null,
  ): string[] {
    const combinedErrors = { ...(newErrors || {}), ...(confirmPasswordErrors || {}) }
    const errorKeys = Object.keys(combinedErrors)

    if (errorKeys.includes('required') || errorKeys.includes('minlength')) {
      // If we see a 'required' key it means we haven't populated the form fields yet,
      // so show the default suggestions.
      return DEFAULT_RECOMMENDATIONS
    } else if (errorKeys.length === 0) {
      // If there are no error keys at all, the password is good,
      // so display the recommendations for a strong password.
      return STRONG_RECOMMENDATIONS
    }
    return compact(
      flatten(
        errorKeys.sort().map(validationKey => {
          const validation = combinedErrors[validationKey]
          if (validationKey === 'weakPassword') {
            return compact([validation.warning, ...validation.suggestions])
          } else if (typeof validation === 'string') {
            return validation
          } else {
            return ''
          }
        }),
      ),
    )
  }
}
